import { Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useErrorInterceptor } from "../../hooks/useErrorInterceptor";
import { useFirestoreNetworkBridge } from "../../hooks/useFirestoreNetworkBridge";
import { UserPopover } from "./UserPopover";
import { ErrorToast } from "./ErrorToast";
import { BottomNav } from "./BottomNav";
import { GlobalLogSheet } from "./GlobalLogSheet";
import { LoggedToast } from "./LoggedToast";
import { LogSheetProvider } from "../../contexts/LogSheetContext";
import { TypeSettingsProvider } from "../../contexts/TypeSettingsContext";
import { Login } from "../../pages/Login";
import { IS_STAGING } from "../../utils/environment";
import { PageBarProvider, usePageBarView } from "../../contexts/PageBarContext";

/**
 * The bar's left side: the way back on a detail page ("‹ Orlando Parks
 * Week"), the page's title on a tab page (phones), else the app's name.
 */
function TopBarLead() {
  const { back, title } = usePageBarView();
  const navigate = useNavigate();
  const badge = IS_STAGING && <span className="env-badge">Staging</span>;
  if (back) {
    const inner = (
      <>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 5l-7 7 7 7" />
        </svg>
        <span>{back.label}</span>
      </>
    );
    const aria = back.kind === "cancel" ? back.label : `Back to ${back.label}`;
    return (
      <div className="header-lead">
        {back.kind === "parent" ? (
          <Link to={back.to} className="header-back" aria-label={aria}>{inner}</Link>
        ) : (
          <button
            type="button"
            className="header-back"
            aria-label={aria}
            onClick={back.kind === "history" ? () => navigate(back.delta) : back.onClick}
          >
            {inner}
          </button>
        )}
        {badge}
      </div>
    );
  }
  return (
    <div className="header-lead">
      <Link to="/" className={`header-title${title ? " header-title-has-page" : ""}`}>
        Trip Tracker
      </Link>
      {title && <span className="header-page-title">{title}</span>}
      {badge}
    </div>
  );
}

function TopBarActionSlot() {
  const { setActionSlot } = usePageBarView();
  return <div className="header-action" ref={setActionSlot} />;
}

export function Layout() {
  const { user, loading } = useAuth();

  // Pipe console.error into the in-app error toast for users with
  // the view_error_details role.
  useErrorInterceptor();

  // Toggle Firestore network based on real connectivity so cached reads
  // resolve instantly when we're on a flaky connection instead of
  // hanging on the long-polling timeout.
  useFirestoreNetworkBridge();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Render the sign-in screen inline instead of behind a /login route.
  // On branch previews with the production PWA installed, Chrome's PWA
  // link capturing would hijack a <Link to="/login"> click and open the
  // installed standalone app (which doesn't know about preview URLs),
  // landing users on a 404. Rendering Login directly avoids any
  // in-scope URL navigation before auth completes.
  if (!user) {
    return <Login />;
  }

  return (
    <TypeSettingsProvider>
      <LogSheetProvider>
        <PageBarProvider>
        <div className="app">
          <header className="header">
            <TopBarLead />
            <nav className="header-nav">
              <div className="header-nav-links">
                <Link to="/trips">My Trips</Link>
                <Link to="/journey">Journey</Link>
                <Link to="/entities">Entities</Link>
                <Link to="/connections">Connections</Link>
              </div>
              <TopBarActionSlot />
              <Link to="/search" className="header-search" aria-label="Search" title="Search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="16.5" y1="16.5" x2="21" y2="21" />
                </svg>
              </Link>
              <UserPopover />
            </nav>
          </header>
          <main className="main">
            <Outlet />
          </main>
          <BottomNav />
          <GlobalLogSheet />
          <LoggedToast />
          <ErrorToast />
        </div>
        </PageBarProvider>
      </LogSheetProvider>
    </TypeSettingsProvider>
  );
}
